import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Form from "../../coreComponents/Form/Form.tsx";
import { login } from "../../services/AuthService.ts";
import { Card, CardContent, Typography } from "@mui/material";

interface LoginProps {
  onLogin: (username: string, password: string) => Promise<void>;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const fields = [
    {
      type: "text",
      name: "username",
      label: "Username",
      placeholder: "Enter your username",
      required: true,
      autoComplete: "username",
    },
    {
      type: "password",
      name: "password",
      label: "Password",
      placeholder: "Enter your password",
      required: true,
      autoComplete: "current-password",
    },
  ];

  const handleLogin = async ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    setIsLoading(true);
    setError(null);

    if (typeof username !== "string" || username.trim() === "") {
      setError("Username must be a string and cannot be empty.");
      setIsLoading(false);
      return;
    }

    if (typeof password !== "string" || password.trim() === "") {
      setError("Password must be a string and cannot be empty.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await login(username, password);
      const { accessToken, refreshToken } = response.data;

      if (accessToken && refreshToken) {
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        navigate("/dashboard");
      } else {
        throw new Error("Invalid credentials");
      }
    } catch (error) {
      console.error("Login failed:", error);
      setError("Invalid credentials, please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50">
      <Card className="w-[500px] h-[500px] mx-4 shadow-lg">
        <CardContent>
          <Typography variant="h5" component="div" align="center" gutterBottom>
            Login
          </Typography>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {error}
            </div>
          )}

          <Form
            fields={fields}
            initialValues={{ username: "", password: "" }}
            onSubmit={handleLogin}
            buttons={[
              {
                id: "submit",
                children: isLoading ? "Logging in..." : "Login",
                type: "submit",
                variant: "contained",
                fullWidth: true,
              },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
